/**
 * IndexedDB-based cache for pointcloud data.
 * Stores raw ArrayBuffer (PLY) or JSON string to avoid re-downloading.
 */

const DB_NAME = "pointcloud-cache";
const DB_VERSION = 1;
const STORE_NAME = "scans";

export type ScanCacheStatus = "not_cached" | "cached" | "downloading";

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Get cached data for a scan URL. Returns null if not cached. */
export async function getCached(url: string): Promise<ArrayBuffer | string | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(url);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

/** Store data in cache. */
export async function putCache(url: string, data: ArrayBuffer | string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const req = store.put(data, url);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/** Check if a URL is cached (without loading the full data). */
export async function isCached(url: string): Promise<boolean> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const req = store.count(IDBKeyRange.only(url));
    req.onsuccess = () => resolve(req.result > 0);
    req.onerror = () => reject(req.error);
  });
}

/** Delete a cached scan. */
export async function deleteCache(url: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const req = store.delete(url);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/** Check cache status for multiple URLs at once. */
export async function getCacheStatuses(urls: string[]): Promise<Map<string, boolean>> {
  const db = await openDB();
  const result = new Map<string, boolean>();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    let pending = urls.length;
    if (pending === 0) { resolve(result); return; }
    for (const url of urls) {
      const req = store.count(IDBKeyRange.only(url));
      req.onsuccess = () => {
        result.set(url, req.result > 0);
        if (--pending === 0) resolve(result);
      };
      req.onerror = () => reject(req.error);
    }
  });
}
